import React from 'react';
import { Link } from 'react-router-dom';
import { 
  MapPinIcon, 
  EnvelopeIcon, 
  HeartIcon,
  ShieldCheckIcon 
} from '@heroicons/react/24/outline';

function Footer() {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    product: [
      { name: 'Find Restaurants', href: '/search' },
      { name: 'Submit Restaurant', href: '/submit' },
      { name: 'About', href: '/about' },
    ],
    resources: [
      { name: 'Kashrus Agencies', href: '/certifications' },
      { name: 'Help & FAQ', href: '/help' },
      { name: 'Contact Us', href: '/contact' },
    ],
    legal: [
      { name: 'Privacy Policy', href: '/privacy' },
      { name: 'Terms of Service', href: '/terms' },
      { name: 'Data Sources', href: '/sources' },
    ],
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Main Footer Content */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Brand Section */}
          <div className="lg:col-span-1 space-y-6">
            <div className="flex items-center space-x-2">
              <div className="bg-primary-600 text-white p-2 rounded-lg">
                <MapPinIcon className="h-6 w-6" />
              </div>
              <div>
                <div className="font-bold text-xl">KosherEats</div>
                <div className="text-sm text-gray-400">Verified Kosher Directory</div>
              </div>
            </div>
            
            <p className="text-gray-300 leading-relaxed">
              The most comprehensive and trusted kosher restaurant directory, 
              helping Jewish families find verified kosher dining options nationwide.
            </p>
            
            <div className="flex items-center space-x-2 text-sm text-gray-400">
              <ShieldCheckIcon className="w-4 h-4" />
              <span>100% Verified Certifications</span>
            </div>
          </div>

          {/* Product Links */}
          <div>
            <h3 className="font-semibold text-white mb-4">Product</h3>
            <ul className="space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Links */}
          <div>
            <h3 className="font-semibold text-white mb-4">Resources</h3>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="font-semibold text-white mb-4">Legal</h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Newsletter Signup */}
        <div className="border-t border-gray-800 pt-8 mb-8">
          <div className="max-w-md mx-auto text-center lg:text-left lg:max-w-none lg:flex lg:items-center lg:justify-between">
            <div className="lg:flex-1">
              <h3 className="text-lg font-semibold text-white mb-2">
                Stay Updated
              </h3>
              <p className="text-gray-300">
                Get notified about new kosher restaurants and features
              </p>
            </div>
            <div className="mt-4 lg:mt-0 lg:ml-8">
              <form className="flex max-w-md">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-white placeholder-gray-400"
                />
                <button
                  type="submit"
                  className="px-6 py-2 bg-primary-600 text-white rounded-r-lg hover:bg-primary-700 transition-colors font-medium"
                >
                  Subscribe
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="flex items-center space-x-4 text-sm text-gray-400">
              <span>© {currentYear} KosherEats. All rights reserved.</span>
            </div>
            
            <div className="flex items-center space-x-6">
              {/* Contact Info */}
              <div className="flex items-center space-x-2 text-sm text-gray-400">
                <EnvelopeIcon className="w-4 h-4" />
                <a href="mailto:hello@koshereats.com" className="hover:text-white transition-colors">
                  hello@kosher.bigcohen.org
                </a>
              </div>
              
              {/* Made with Love */}
              <div className="flex items-center space-x-2 text-sm text-gray-400">
                <span>Made with</span>
                <HeartIcon className="w-4 h-4 text-red-500" />
                <span>for the Jewish community</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Attribution */}
      <div className="bg-gray-950 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="text-center text-xs text-gray-500">
            <p>
              Data sourced from YeahThatsKosher, Jewish Food Experience, Google Places, and Yelp. 
              Restaurant certifications verified directly with kashrus agencies.
            </p>
            <p className="mt-1">
              This service is provided as-is. Always verify current certification status with the restaurant.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;